<template>
    <NuxtLayout name="malt-full-layout">
        <VJoyTemplate class="sitemap">
            <template #template-title>
                <h1>{{ $t('sitemap.title') }}</h1>
            </template>
            <template #template-subtitle>{{ $t('acquisition.banner.subtitle') }}</template>
            <template #template-main>
                <SearchBlock page="sitemap" />

                <section class="sitemap-section">
                    <h2>{{ $t('sitemap.company') }}</h2>
                    <ul class="sitemap__list">
                        <li>
                            <VJoyLink :href="$withLocalePrefix('/help')">{{ $t('sitemap.company.search') }}</VJoyLink>
                        </li>
                        <li>
                            <VJoyLink :href="$withLocalePrefix('/media')">{{ $t('sitemap.company.press') }}</VJoyLink>
                        </li>
                        <li>
                            <VJoyLink :href="faqLink" target="_blank" rel="noopener">{{ $t('sitemap.faq') }}</VJoyLink>
                        </li>
                        <li>
                            <VJoyLink :href="$withLocalePrefix('/all-freelances')">{{ $t('sitemap.all.freelancers') }}</VJoyLink>
                        </li>
                        <li>
                            <VJoyLink :href="$withLocalePrefix('/freelancers/categories')">
                                {{ $t('footer.sitemap.freelancers.categories.label') }}
                            </VJoyLink>
                        </li>
                        <li>
                            <VJoyLink :href="$withLocalePrefix('/freelancers/tags')">{{ $t('footer.sitemap.freelancers.tags.label') }}</VJoyLink>
                        </li>
                        <li>
                            <VJoyLink :href="$withLocalePrefix('/freelancers/locations')">
                                {{ $t('footer.sitemap.freelancers.locations.label') }}
                            </VJoyLink>
                        </li>
                        <li>
                            <VJoyLink :href="$withLocalePrefix('/corporate')">{{ $t('sitemap.corporate') }}</VJoyLink>
                        </li>
                    </ul>
                </section>
                <section class="sitemap-section">
                    <h2>{{ $t('sitemap.freelancers') }}</h2>
                    <ul class="sitemap__list">
                        <li v-if="!$ff('french-content') && barometerUrl">
                            <VJoyLink :href="barometerUrl ? $withLocalePrefix(barometerUrl) : undefined">{{ $t('sitemap.barometer') }}</VJoyLink>
                        </li>
                        <li>
                            <VJoyLink :href="$withLocalePrefix('/profile')">{{ $t('sitemap.publish.profile') }}</VJoyLink>
                        </li>
                        <li>
                            <VJoyLink :href="faqLink" target="_blank" rel="noopener">{{ $t('sitemap.faq') }}</VJoyLink>
                        </li>
                        <li>
                            <VJoyLink :href="$withLocalePrefix('/super-malter-program')">{{ $t('sitemap.status.freelancer.star') }}</VJoyLink>
                        </li>
                    </ul>
                </section>
                <section class="sitemap-section">
                    <h2>{{ $t('sitemap.about.hopwork') }}</h2>
                    <ul class="sitemap__list">
                        <li>
                            <VJoyLink :href="$withLocalePrefix('/contact')">{{ $t('sitemap.contact') }}</VJoyLink>
                        </li>
                    </ul>
                </section>
            </template>
        </VJoyTemplate>
    </NuxtLayout>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {createError, useAsyncData} from '#app';
    import {SearchBlock} from '#components';
    import {useHostSettings, useLocale, useSeoMeta} from '#imports';
    import {VJoyTemplate, VJoyLink} from '@maltjoy/core-vue';
    import {useSeo} from '~/composables/useSeo';

    const {locale} = useLocale();
    const faqLink = computed(() => 'https://help.malt.com/kb/' + locale.value.split('-')[0]);

    const {data: hostSettings, error} = await useAsyncData('sitemap-init', () => useHostSettings(), {
        deep: true,
    });
    if (error.value) {
        throw createError(error.value);
    }

    const barometerUrl = computed(() => hostSettings.value?.barometerUrl);

    useSeo();
    useSeoMeta({robots: null});
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .sitemap {
        :deep(.joy-template-slot-title) {
            h1 {
                font-family: var(--joy-font-family-title) !important;
                font-size: var(--joy-font-size-secondary-600) !important;
            }
        }

        :deep(.joy-template-slot-subtitle) {
            font-size: var(--joy-font-size-primary-600) !important;
        }

        &-section {
            margin-bottom: var(--joy-core-spacing-12);

            h2 {
                font-family: var(--joy-font-family-title);
                font-size: var(--joy-font-size-secondary-200);
                margin-bottom: var(--joy-core-spacing-4);
            }
        }

        &__list {
            list-style: none;
            padding: 0;
            margin: calc(var(--joy-core-spacing-3) * -1);
            display: flex;
            flex-wrap: wrap;

            > li {
                flex-basis: 25%;
                padding: var(--joy-core-spacing-3);
            }

            @include mq.screen_xs {
                flex-direction: column;
            }
        }
    }
</style>
